import { lazy, type ComponentType } from "react"

/**
 * After a deploy, the admin server only has the new build's files. A tab that was opened before the
 * deploy still asks for the old ones when it opens a page it hasn't loaded yet (Apps, Analytics,
 * order edit…), gets a 404 and showed "Failed to fetch dynamically imported module". The fix a
 * person would make is reloading the page, so the admin does that itself, once: the reload fetches
 * the new index.html, which points at the new files.
 *
 * Guarded so a real outage can't loop: at most one automatic reload per 30 seconds per tab.
 */
const KEY = "terify-chunk-reload-at"
/** Set once a reload has been asked for: anything still loading just waits for it. */
let reloading = false

export function isReloading() {
  return reloading
}

export function isChunkLoadError(e: unknown): boolean {
  const msg = String((e as { message?: unknown })?.message ?? e ?? "")
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError|Loading chunk \S+ failed/i.test(msg)
}

/** Reloads the page once if it hasn't just done so. Returns false when it already tried (show an error instead). */
export function reloadForNewVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0)
    if (Date.now() - last < 30_000) return false
    sessionStorage.setItem(KEY, String(Date.now()))
  } catch {
    // storage blocked: reload anyway (a loop is still bounded by the browser's own back-off)
  }
  reloading = true
  window.location.reload()
  return true
}

/** React.lazy that reloads into the new version when the old chunk is gone. */
export function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory().catch((e) => {
      // Vite's preload handler below may already have started the reload; the import then resolves
      // empty and the factory fails on it. Either way, wait for the new version.
      if (reloading || (isChunkLoadError(e) && reloadForNewVersion())) {
        // Keep Suspense showing its placeholder until the page reloads.
        return new Promise<{ default: T }>(() => {})
      }
      throw e
    }),
  )
}

/** Vite's own signal that a preloaded file failed (CSS or a shared chunk): same remedy. */
export function installChunkReload() {
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadForNewVersion()) event.preventDefault()
  })
}
